/**
 * UserSelector - Componente seletor de usuários/responsáveis
 * 
 * Componente reutilizável para selecionar usuários do tenant em formulários.
 * Baseado no SellerSelector para manter consistência visual.
 * 
 * @version 1.0.0
 */

import React from 'react';
import { Check, ChevronsUpDown, UserCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';

// Tipo para usuário do tenant
interface TenantUser {
  id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
}

// Props do componente
interface UserSelectorProps {
  value: string | null | undefined;
  onSelect: (userId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
}

/**
 * Componente para seleção de usuário/responsável com busca e avatar
 */
export function UserSelector({
  value,
  onSelect,
  placeholder = 'Selecione um responsável...',
  disabled = false,
  className,
  allowClear = true,
}: UserSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const { currentTenant } = useAuth();

  // v1.1.0: Migrado para hook unificado `useTenantMembers` (RPC server-side).
  // Cache compartilhado com outros pickers — evita SELECTs repetidos a
  // tenant_members?tenant_id=eq.<uuid>.
  const { data: rawMembers = [], isLoading } = useTenantMembers({ onlyActive: true });
  const users: TenantUser[] = rawMembers.map((m) => ({
    id: m.user_id,
    name: m.full_name || m.email || 'Usuário',
    email: m.email,
    avatar_url: m.avatar_url,
  }));

  // Usuário selecionado atualmente
  const selectedUser = users.find(u => u.id === value);

  // Handler para seleção
  const handleSelect = (userId: string) => {
    if (userId === value) {
      // Clicou no mesmo - limpa seleção
      onSelect(null);
    } else {
      onSelect(userId);
    }
    setOpen(false);
  };

  // Handler para limpar seleção
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  if (isLoading) {
    return <Skeleton className="h-9 w-full" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal h-9 border-border/50",
            !selectedUser && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 truncate">
            {selectedUser ? (
              <>
                <SmartAvatar
                  src={selectedUser.avatar_url}
                  name={selectedUser.name}
                  size="xs"
                  use3DFallback
                />
                <span className="truncate">{selectedUser.name}</span>
              </>
            ) : (
              <>
                <UserCheck className="h-4 w-4 opacity-50" />
                <span>{placeholder}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {allowClear && selectedUser && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar usuário..." />
          <CommandList>
            <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
            <CommandGroup>
              {users.map(user => (
                <CommandItem
                  key={user.id}
                  value={user.name}
                  onSelect={() => handleSelect(user.id)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <SmartAvatar
                    src={user.avatar_url}
                    name={user.name}
                    size="sm"
                    use3DFallback
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">{user.name}</span>
                    {user.email && (
                      <span className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      value === user.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
